import React, { useState, useEffect } from 'react';
import api from '../api/api';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  GridLegacy as Grid,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Payment as PaymentIcon,
  AccountBalance as AccountBalanceIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { MpesaPaymentDialog } from './MpesaPaymentDialog';

interface Subscription {
  id: number;
  plan_name: string;
  subscription_type: 'term' | 'year';
  start_date: string;
  end_date: string;
  status: 'active' | 'expired' | 'cancelled';
  payment_status: 'pending' | 'paid' | 'failed';
  amount: number;
}

interface Invoice {
  id: number;
  invoice_number: string;
  issue_date: string;
  due_date: string;
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';
  total_amount: number;
  subscription_id?: number;
}

interface MpesaStatus {
  enabled: boolean;
  configured: boolean;
  environment: string;
  business_shortcode: string;
  ready: boolean;
}

export const SchoolPaymentInitiation: React.FC = () => {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [mpesaStatus, setMpesaStatus] = useState<MpesaStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Payment dialog states
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [paymentType, setPaymentType] = useState<'subscription' | 'invoice'>('subscription');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch M-Pesa status
      const mpesaResponse = await api.get('/settings/mpesa/status');
      setMpesaStatus(mpesaResponse.data);

      // Fetch school subscriptions
      const subscriptionsResponse = await api.get('/subscriptions/');
      setSubscriptions(subscriptionsResponse.data);

      // Fetch school invoices
      const invoicesResponse = await api.get('/invoices/');
      setInvoices(invoicesResponse.data);
    } catch (err) {
      setError('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentInitiation = (subscription?: Subscription, invoice?: Invoice) => {
    if (subscription) {
      setSelectedSubscription(subscription);
      setSelectedInvoice(null);
      setPaymentType('subscription');
    } else if (invoice) {
      setSelectedInvoice(invoice);
      setSelectedSubscription(null);
      setPaymentType('invoice');
    }
    setPaymentDialogOpen(true);
  };

  const handlePaymentSuccess = () => {
    setPaymentDialogOpen(false);
    setSuccess('Payment initiated successfully! Check your phone for M-Pesa prompt.');
    fetchData(); // Refresh data
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
      case 'paid':
        return 'success';
      case 'pending':
      case 'sent':
        return 'warning';
      case 'expired':
      case 'overdue':
      case 'failed':
        return 'error';
      case 'cancelled':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
      case 'paid':
        return <CheckCircleIcon />;
      case 'pending':
      case 'sent':
        return <ScheduleIcon />;
      case 'expired':
      case 'overdue':
      case 'failed':
        return <ErrorIcon />;
      default:
        return <ReceiptIcon />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const pendingSubscriptions = subscriptions.filter(sub => sub.payment_status === 'pending');
  const pendingInvoices = invoices.filter(inv => inv.status === 'sent' || inv.status === 'overdue');

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <AccountBalanceIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h5">School Payments</Typography>
          </Box>

          {/* M-Pesa Status */}
          {mpesaStatus && (
            <Alert 
              severity={mpesaStatus.ready ? "success" : "warning"} 
              sx={{ mb: 3 }}
              icon={mpesaStatus.ready ? <CheckCircleIcon /> : <ErrorIcon />}
            >
              <Typography variant="body2">
                <strong>M-Pesa Status:</strong> {mpesaStatus.ready ? 'Ready' : 'Not Configured'}
              </Typography>
              {mpesaStatus.ready && (
                <Typography variant="body2">
                  Environment: {mpesaStatus.environment} | Shortcode: {mpesaStatus.business_shortcode}
                </Typography>
              )}
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* Pending Subscriptions */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Pending Subscriptions ({pendingSubscriptions.length})
                  </Typography>
                  
                  {pendingSubscriptions.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">
                      No pending subscriptions
                    </Typography>
                  ) : (
                    <List>
                      {pendingSubscriptions.map((subscription) => (
                        <ListItem key={subscription.id} divider>
                          <ListItemText
                            primary={subscription.plan_name}
                            secondary={
                              <Box>
                                <Typography variant="body2">
                                  {subscription.subscription_type} | KES {subscription.amount.toLocaleString()}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                  Due: {formatDate(subscription.end_date)}
                                </Typography>
                              </Box>
                            }
                          />
                          <ListItemSecondaryAction>
                            <Chip
                              icon={getStatusIcon(subscription.payment_status)}
                              label={subscription.payment_status.toUpperCase()}
                              color={getStatusColor(subscription.payment_status) as any}
                              size="small"
                              sx={{ mr: 1 }}
                            />
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<PaymentIcon />}
                              onClick={() => handlePaymentInitiation(subscription)}
                              disabled={!mpesaStatus?.ready}
                            >
                              Pay
                            </Button>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Pending Invoices */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Pending Invoices ({pendingInvoices.length})
                  </Typography>
                  
                  {pendingInvoices.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">
                      No pending invoices
                    </Typography>
                  ) : (
                    <List>
                      {pendingInvoices.map((invoice) => (
                        <ListItem key={invoice.id} divider>
                          <ListItemText
                            primary={`Invoice #${invoice.invoice_number}`}
                            secondary={
                              <Box>
                                <Typography variant="body2">
                                  KES {invoice.total_amount.toLocaleString()}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                  Due: {formatDate(invoice.due_date)}
                                </Typography>
                              </Box>
                            }
                          />
                          <ListItemSecondaryAction>
                            <Chip
                              icon={getStatusIcon(invoice.status)}
                              label={invoice.status.toUpperCase()}
                              color={getStatusColor(invoice.status) as any}
                              size="small"
                              sx={{ mr: 1 }}
                            />
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<PaymentIcon />}
                              onClick={() => handlePaymentInitiation(undefined, invoice)}
                              disabled={!mpesaStatus?.ready}
                            >
                              Pay
                            </Button>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* All Subscriptions */}
          <Typography variant="h6" gutterBottom>
            All Subscriptions
          </Typography>
          <Card variant="outlined">
            <CardContent>
              {subscriptions.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  No subscriptions found
                </Typography>
              ) : (
                <List>
                  {subscriptions.map((subscription) => (
                    <ListItem key={subscription.id} divider>
                      <ListItemText
                        primary={subscription.plan_name}
                        secondary={
                          <Box>
                            <Typography variant="body2">
                              {subscription.subscription_type} | KES {subscription.amount.toLocaleString()}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              {formatDate(subscription.start_date)} - {formatDate(subscription.end_date)}
                            </Typography>
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <Chip
                          icon={getStatusIcon(subscription.status)}
                          label={subscription.status.toUpperCase()}
                          color={getStatusColor(subscription.status) as any}
                          size="small"
                          sx={{ mr: 1 }}
                        />
                        {subscription.payment_status === 'pending' && (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<PaymentIcon />}
                            onClick={() => handlePaymentInitiation(subscription)}
                            disabled={!mpesaStatus?.ready}
                          >
                            Pay
                          </Button>
                        )}
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </CardContent>
      </Card>

      {/* M-Pesa Payment Dialog */}
      {paymentDialogOpen && (
        <MpesaPaymentDialog
          open={paymentDialogOpen}
          onClose={() => setPaymentDialogOpen(false)}
          onSuccess={handlePaymentSuccess}
          subscriptionId={selectedSubscription?.id}
          invoiceId={selectedInvoice?.id}
          amount={selectedSubscription?.amount || selectedInvoice?.total_amount || 0}
          title={`Pay ${paymentType === 'subscription' ? 'Subscription' : 'Invoice'}`}
          description={
            paymentType === 'subscription' 
              ? `Pay for ${selectedSubscription?.plan_name} subscription`
              : `Pay invoice #${selectedInvoice?.invoice_number}`
          }
        />
      )}
    </Box>
  );
}; 